import { Controller } from "stimulus";
import { Modal } from "bootstrap";

export default class extends Controller {
  static targets = ["values", "set", "name", "description"];

  initialize() {
    this.labValues = new Map();
    this.isNewSet = false;

    // Listen for modal show event
    document
      .getElementById("labsModal")
      .addEventListener("show.bs.modal", (event) => {
        // Check if this is triggered by the Add Labs button
        if (event.relatedTarget?.hasAttribute("data-bs-toggle")) {
          this.isNewSet = true;
          this.currentEditingSet = null;
          this.currentSetIndex = this.getNextSetIndex();

          // Clear the labValues map
          this.labValues = new Map();

          // Clear all modal inputs
          const modalInputs = document.querySelectorAll(
            '#labsModal input[type="number"]'
          );
          modalInputs.forEach((input) => {
            input.value = "";
          });

          // Clear name and description inputs
          if (this.hasNameTarget) {
            this.nameTarget.value = "";
          }
          if (this.hasDescriptionTarget) {
            this.descriptionTarget.value = "";
          }
        }
      });
  }

  editSet(event) {
    // Don't trigger if clicking the remove button
    if (event.target.closest('[data-action="click->lab-set#removeSet"]')) {
      return;
    }

    this.isNewSet = false;
    // Find the parent card element from the edit button
    this.currentEditingSet = event.target.closest(
      '[data-lab-set-target="set"]'
    );

    // Get the lab set index from the hidden id field
    const idField = this.currentEditingSet.querySelector(
      'input[name*="[scenario_lab_sets_attributes]"][name*="[id]"]'
    );
    this.currentSetIndex = idField
      ? idField.name.match(/scenario_lab_sets_attributes\]\[(\d+)\]/)[1]
      : "0";

    // Reset the labValues map for the new edit session
    this.labValues = new Map();

    // Store all existing values from the current set
    // First, get all grouping headers to build a map of parameter IDs to groupings
    const groupingMap = new Map();
    let currentGrouping = null;

    // Process all children in order to capture grouping information
    const listItems =
      this.currentEditingSet.querySelectorAll(".list-group > li");
    listItems.forEach((item) => {
      if (item.classList.contains("lab-groupings")) {
        // This is a grouping header
        currentGrouping = item.querySelector("div").textContent.trim();
      } else if (item.hasAttribute("data-param-id") && currentGrouping) {
        // This is a parameter under the current grouping
        groupingMap.set(item.dataset.paramId, currentGrouping);
      }
    });

    // Now process the parameter values with their correct groupings
    const existingValues =
      this.currentEditingSet.querySelectorAll("li[data-param-id]");
    existingValues.forEach((valueDiv) => {
      const paramId = valueDiv.dataset.paramId;
      const value = valueDiv.dataset.paramValue;
      const formFields = valueDiv.querySelectorAll("input");
      const fieldData = {};
      formFields.forEach((field) => {
        const nameMatch = field.name.match(/\[(\w+)\]$/);
        if (nameMatch) {
          fieldData[nameMatch[1]] = field.value;
        }
        const nestedMatch = field.name.match(
          /lab_parameter_scenario_lab_sets_attributes\]\[(\d+)\]/
        );
        if (nestedMatch) {
          fieldData.nestedIndex = nestedMatch[1];
        }
      });

      // Extract name and unit from the displayed text
      const nameCell = valueDiv.querySelector(".col-md-8");
      const valueCell = valueDiv.querySelector(".col-md-4");
      const name = nameCell?.textContent.trim() || "";
      const valueText = valueCell?.textContent.trim() || "";
      // Unit is everything after the value number
      const unit = valueText.replace(value, "").trim();

      this.labValues.set(paramId, {
        value,
        name: name,
        unit: unit,
        id: fieldData.id,
        nestedIndex: fieldData.nestedIndex,
        grouping: groupingMap.get(paramId) || "Other",
        existingFields: fieldData,
      });
    });

    // Populate modal inputs
    const modalInputs = document.querySelectorAll(
      '#labsModal input[type="number"]'
    );
    modalInputs.forEach((input) => {
      const paramId = input.dataset.paramId;
      const existingData = this.labValues.get(paramId);
      input.value = existingData ? existingData.value : "";
    });

    // Get and set name and description
    const nameField = this.currentEditingSet.querySelector(
      'input[name$="[name]"]'
    );
    const descriptionField = this.currentEditingSet.querySelector(
      'input[name$="[description]"]'
    );

    if (this.hasNameTarget && nameField) {
      this.nameTarget.value = nameField.value;
    }
    if (this.hasDescriptionTarget && descriptionField) {
      this.descriptionTarget.value = descriptionField.value;
    }

    // Open modal using Bootstrap 5's Modal
    const modalElement = document.getElementById("labsModal");
    const modal = new Modal(modalElement);
    modal.show();
  }

  updateValue(event) {
    const input = event.target;
    const paramId = input.dataset.paramId;
    const paramName = input.dataset.paramName;
    const grouping =
      input.dataset.grouping ||
      input
        .closest(".card")
        .querySelector(".card-header h5")
        .textContent.trim();
    const unit = input.dataset.unit || "";
    const value = input.value;

    // Check if we already have this parameter in our map
    const existingData = this.labValues.get(paramId);

    if (value) {
      this.labValues.set(paramId, {
        value,
        name: paramName,
        // Preserve existing grouping if available, otherwise use the one from the input
        grouping: existingData?.grouping || grouping,
        unit: unit,
        id: existingData?.id || null,
        nestedIndex: existingData?.nestedIndex || null,
      });
    } else {
      this.labValues.delete(paramId);
    }

    // For debugging
    console.log(`Updated labValues for ${paramName}:`, this.labValues);
  }

  saveChanges(event) {
    event.preventDefault();

    // For debugging
    console.log("Saving changes with labValues:", this.labValues);

    if (this.isNewSet) {
      this.createNewSet();
    } else if (this.currentEditingSet) {
      this.updateExistingSet();
    }

    // Close modal using Bootstrap 5's Modal
    const modalElement = document.getElementById("labsModal");
    const modal = Modal.getInstance(modalElement);
    modal.hide();

    // Remove the modal backdrop
    const backdrop = document.querySelector(".modal-backdrop");
    if (backdrop) {
      backdrop.remove();
    }

    // Remove the modal-open class from body and restore scrolling
    document.body.classList.remove("modal-open");
    document.body.style.overflow = "auto";
    document.body.style.paddingRight = "0";
    document.documentElement.style.overflow = "auto";
    document.documentElement.style.paddingRight = "0";

    // Force a reflow to ensure styles are applied
    document.body.offsetHeight;

    // Reset state
    this.isNewSet = false;
    this.currentEditingSet = null;
  }

  createNewSet() {
    const name = this.hasNameTarget ? this.nameTarget.value : "";
    const description = this.hasDescriptionTarget
      ? this.descriptionTarget.value
      : "";

    // Create new set container
    const newSet = document.createElement("div");
    newSet.className = "vs-card-full mb-3 framed";
    newSet.dataset.labSetTarget = "set";

    // Create the header and body structure
    newSet.innerHTML = `
      <input type="hidden" name="scenario[scenario_lab_sets_attributes][${this.currentSetIndex}][id]" value="">
      <input type="hidden" name="scenario[scenario_lab_sets_attributes][${this.currentSetIndex}][_destroy]" value="false">
      <div class="vs-card-header">
        Lab Set
        <div style="display: flex; gap: 8px;">
          <button type="button" class="btn btn-sm btn-primary" data-action="click->lab-set#editSet">
            <i class="fa-solid fa-pen" style="margin-right: 6px;"></i>
            EDIT LAB LIST
          </button>
          <button type="button" class="btn btn-sm btn-danger" data-action="click->lab-set#removeSet">
            <i class="fa fa-trash"></i>
          </button>
        </div>
      </div>
      <div class="vs-card-body">
        <div class="row mb-3">
          <div class="col-md-6">
            <div class="form-group string">
              <label class="form-label" for="scenario_scenario_lab_sets_attributes_${this.currentSetIndex}_name">Name</label>
              <input class="form-control string" type="text" 
                     name="scenario[scenario_lab_sets_attributes][${this.currentSetIndex}][name]" 
                     id="scenario_scenario_lab_sets_attributes_${this.currentSetIndex}_name"
                     value="${name}">
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group string">
              <label class="form-label" for="scenario_scenario_lab_sets_attributes_${this.currentSetIndex}_description">Comments</label>
              <input class="form-control string" type="text" 
                     name="scenario[scenario_lab_sets_attributes][${this.currentSetIndex}][description]" 
                     id="scenario_scenario_lab_sets_attributes_${this.currentSetIndex}_description"
                     value="${description}">
            </div>
          </div>
        </div>
        <ul class="list-group" data-lab-set-target="values" style="list-style: none; padding: 0; margin: 0;">
        </ul>
      </div>
    `;

    // Group parameters by their grouping
    const groupedParams = this.groupParametersByGrouping();
    console.log("GROUPED PARAMS", groupedParams);

    // Add parameter values grouped by their categories
    const valuesContainer = newSet.querySelector(
      '[data-lab-set-target="values"]'
    );
    this.renderGroupedParameters(groupedParams, valuesContainer);

    // Add the new set to the container
    const setsContainer = document.getElementById("lab-sets-container");
    setsContainer.appendChild(newSet);
  }

  updateExistingSet() {
    if (!this.currentEditingSet) return;

    // Update name and description if they exist
    const nameField = this.currentEditingSet.querySelector(
      'input[name$="[name]"]'
    );
    const descriptionField = this.currentEditingSet.querySelector(
      'input[name$="[description]"]'
    );

    if (nameField && this.hasNameTarget) {
      nameField.value = this.nameTarget.value;
    }
    if (descriptionField && this.hasDescriptionTarget) {
      descriptionField.value = this.descriptionTarget.value;
    }

    // Get the values container for the current set
    const valuesContainer = this.currentEditingSet.querySelector(
      '[data-lab-set-target="values"]'
    );
    if (!valuesContainer) return; // Exit if we can't find the values container

    // Before clearing, collect all existing parameter IDs and their data
    const existingParams = new Map();
    this.currentEditingSet
      .querySelectorAll("li[data-param-id]")
      .forEach((item) => {
        const paramId = item.dataset.paramId;
        const hiddenInputs = item.querySelectorAll("input[type='hidden']");

        // Extract the ID and nested index from hidden inputs
        const idInput = Array.from(hiddenInputs).find(
          (input) =>
            input.name.includes("[id]") &&
            !input.name.includes("[lab_parameter_id]")
        );
        const nestedIndex = idInput?.name.match(
          /lab_parameter_scenario_lab_sets_attributes\]\[(\d+)\]/
        )?.[1];

        existingParams.set(paramId, {
          item,
          hiddenInputs,
          id: idInput?.value || null,
          nestedIndex: nestedIndex,
        });
      });

    // Clear existing values container to rebuild it with updated values
    valuesContainer.innerHTML = "";

    // Mark records for destruction if they're not in the updated labValues
    existingParams.forEach((existingData, paramId) => {
      if (!this.labValues.has(paramId)) {
        // This parameter was removed - mark it for destruction
        if (existingData.id && existingData.nestedIndex) {
          // Only mark for destruction if it has an ID (exists in database)
          const destroyContainer = document.createElement("div");
          destroyContainer.style.display = "none";
          destroyContainer.innerHTML = `
            <input type="hidden" 
                   name="scenario[scenario_lab_sets_attributes][${this.currentSetIndex}][lab_parameter_scenario_lab_sets_attributes][${existingData.nestedIndex}][id]" 
                   value="${existingData.id}">
            <input type="hidden" 
                   name="scenario[scenario_lab_sets_attributes][${this.currentSetIndex}][lab_parameter_scenario_lab_sets_attributes][${existingData.nestedIndex}][_destroy]" 
                   value="1">
          `;
          valuesContainer.appendChild(destroyContainer);
        }
      } else {
        // This parameter still exists - make sure we preserve its nested index
        const labValue = this.labValues.get(paramId);
        if (!labValue.nestedIndex && existingData.nestedIndex) {
          // Preserve the existing nested index if we don't already have one
          labValue.nestedIndex = existingData.nestedIndex;
          labValue.id = existingData.id;
        }
      }
    });

    // Group parameters by their grouping
    const groupedParams = this.groupParametersByGrouping();

    // Add parameter values grouped by their categories
    this.renderGroupedParameters(groupedParams, valuesContainer);
  }

  // Helper method to group parameters by their grouping
  groupParametersByGrouping() {
    const groupedParams = new Map();
    this.labValues.forEach((data, paramId) => {
      const grouping = data.grouping || "Other";
      if (!groupedParams.has(grouping)) {
        groupedParams.set(grouping, []);
      }
      groupedParams.get(grouping).push({ paramId, ...data });
    });
    return groupedParams;
  }

  // Helper method to render grouped parameters to a container
  renderGroupedParameters(groupedParams, container) {
    // Sort the groupings alphabetically, but keep 'Other' at the end if it exists
    const sortedGroupings = Array.from(groupedParams.keys()).sort((a, b) => {
      if (a === "Other") return 1;
      if (b === "Other") return -1;
      return a.localeCompare(b);
    });

    // Initialize the nested index counter for new parameters
    let nextNestedIndex = null;

    sortedGroupings.forEach((grouping) => {
      if (grouping !== "Other") {
        const groupHeader = document.createElement("li");
        groupHeader.className = "lab-group-item lab-groupings";
        groupHeader.style.listStyle = "none";
        groupHeader.style.color = "#E0E8F9";
        groupHeader.innerHTML = `<div class="mb-0 p-1">${grouping}</div>`;
        container.appendChild(groupHeader);
      }

      // Sort parameters alphabetically by name within each group
      const sortedParams = groupedParams
        .get(grouping)
        .sort((a, b) => a.name.localeCompare(b.name));

      sortedParams.forEach((data) => {
        // If this parameter doesn't have a nestedIndex, assign one and increment
        if (!data.nestedIndex) {
          if (nextNestedIndex === null) {
            nextNestedIndex = this.getNextNestedIndex();
          }
          data.nestedIndex = nextNestedIndex;
          nextNestedIndex++;
        }

        const paramDiv = this.createParameterDiv(data.paramId, data);
        container.appendChild(paramDiv);
      });
    });
  }

  getNextSetIndex() {
    const existingSets = document.querySelectorAll(
      'input[name*="[scenario_lab_sets_attributes]"][name*="[id]"]'
    );
    return existingSets.length;
  }

  removeSet(event) {
    event.stopPropagation(); // Prevent editSet from triggering
    const card = event.target.closest(".vs-card-full");
    const destroyInput = card.querySelector('input[name*="_destroy"]');
    destroyInput.value = "1";
    card.style.display = "none";
  }

  createParameterDiv(paramId, data) {
    const li = document.createElement("li");
    li.className = "list-group-item";
    li.dataset.paramId = paramId;
    li.dataset.paramValue = data.value;
    li.dataset.grouping = data.grouping || "Other";
    li.style.listStyle = "none";

    // At this point, data.nestedIndex should always be set by renderGroupedParameters
    const nestedIndex = data.nestedIndex;

    li.innerHTML = `
      <input type="hidden" 
             name="scenario[scenario_lab_sets_attributes][${
               this.currentSetIndex
             }][lab_parameter_scenario_lab_sets_attributes][${nestedIndex}][id]" 
             value="${data.id || ""}">
      <input type="hidden" 
             name="scenario[scenario_lab_sets_attributes][${
               this.currentSetIndex
             }][lab_parameter_scenario_lab_sets_attributes][${nestedIndex}][lab_parameter_id]" 
             value="${paramId}">
      <input type="hidden" 
             name="scenario[scenario_lab_sets_attributes][${
               this.currentSetIndex
             }][lab_parameter_scenario_lab_sets_attributes][${nestedIndex}][value]" 
             value="${data.value}">
      <div class="row">
        <div class="col-md-8 text-start">
          ${data.name}
        </div>
        <div class="col-md-4 text-end">
          ${data.value}
          ${data.unit || ""}
        </div>
      </div>
    `;

    return li;
  }

  getNextNestedIndex() {
    // For new records, Rails expects sequential integer indices
    // Find all existing nested indices in the current set
    if (!this.currentEditingSet && !this.isNewSet) {
      return 0;
    }

    // Get all existing nested indices across all lab sets
    const allNestedInputs = document.querySelectorAll(
      'input[name*="[lab_parameter_scenario_lab_sets_attributes]"][name*="[id]"]'
    );

    let maxIndex = -1;
    allNestedInputs.forEach((input) => {
      const match = input.name.match(
        /lab_parameter_scenario_lab_sets_attributes\]\[(\d+)\]/
      );
      if (match) {
        const index = parseInt(match[1], 10);
        if (index > maxIndex) {
          maxIndex = index;
        }
      }
    });

    return maxIndex + 1;
  }
}
